import { useState, useCallback } from 'react';
import { createRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { format, subHours, subDays } from 'date-fns';
import { Route as rootRoute } from '../__root';
import { Layout } from '@/client/layouts/layout';
import { API_BASE_URL } from '@/client/config';
import { usePipelineLogsStore } from '@/client/store/pipelineLogsStore';
import { Button } from '@/client/components/ui/button';
import { Input } from '@/client/components/ui/input';
import { Badge } from '@/client/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import { Search, Loader2, AlertCircle, ChevronDown, Download } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/client/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/client/components/ui/popover';
import { Checkbox } from '@/client/components/ui/checkbox';
import { cn } from '@/client/lib/utils';
import { PIPELINE_CONFIG, type PipelineSearchType, type PipelineLogType } from '@shared/types';

// Quick range presets (max ~3 months per API constraint)
const QUICK_RANGES = [
  { label: '1h', hours: 1 },
  { label: '6h', hours: 6 },
  { label: '12h', hours: 12 },
  { label: '24h', hours: 24 },
  { label: '48h', hours: 48 },
  { label: '72h', hours: 72 },
  { label: '7d', hours: 168 },
  { label: '1m', hours: 730 },
  { label: '3m', hours: 2191 },
] as const;

type QuickRangeLabel = typeof QUICK_RANGES[number]['label'];

const SEARCH_TYPES: { value: PipelineSearchType; label: string }[] = [
  { value: 'eventID', label: 'Event ID' },
  { value: 'clientID', label: 'Client ID' },
  { value: 'encoderID', label: 'Encoder ID' },
  { value: 'assetID', label: 'Asset ID' },
  { value: 'freetext', label: 'Free Text' },
];

const LOG_TYPES: { value: PipelineLogType; label: string; color: string }[] = [
  { value: 'critical', label: 'Critical', color: 'bg-red-500' },
  { value: 'error', label: 'Error', color: 'bg-red-400' },
  { value: 'warning', label: 'Warning', color: 'bg-amber-400' },
  { value: 'info', label: 'Info', color: 'bg-blue-400' },
  { value: 'debug', label: 'Debug', color: 'bg-gray-400' },
];

// Quick search presets for common debugging scenarios (Stories 6, 7, 8, 10)
type QuickPreset = {
  label: string;
  description: string;
  searchType: PipelineSearchType;
  searchValue: string;
  logTypes: PipelineLogType[];
  range: QuickRangeLabel;
};

const QUICK_PRESETS: QuickPreset[] = [
  {
    label: 'Health Check',
    description: 'Recent errors, warnings & critical issues',
    searchType: 'freetext',
    searchValue: 'error OR warning OR fail',
    logTypes: ['critical', 'error', 'warning'],
    range: '24h',
  },
  {
    label: 'Infrastructure Errors',
    description: 'Critical + error level messages',
    searchType: 'freetext',
    searchValue: 'EC2 OR AWS OR scale OR egress',
    logTypes: ['critical', 'error'],
    range: '24h',
  },
  {
    label: 'Egress Capacity',
    description: 'NO EGRESS and scaling warnings',
    searchType: 'freetext',
    searchValue: 'NO EGRESS',
    logTypes: ['warning', 'error'],
    range: '24h',
  },
  {
    label: 'FTP Failures',
    description: 'CDN sync and FTP transfer issues',
    searchType: 'freetext',
    searchValue: 'ftp_',
    logTypes: ['critical', 'error'],
    range: '48h',
  },
  {
    label: 'Transcode Stuck',
    description: 'Transcode processing issues',
    searchType: 'freetext',
    searchValue: 'transcode',
    logTypes: ['error', 'warning'],
    range: '24h',
  },
];

type PipelineLogsSearch = {
  searchType?: PipelineSearchType;
  searchValue?: string;
  range?: QuickRangeLabel;
  logTypes?: string; // comma-separated list of severity types
};

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'pipeline-logs',
  validateSearch: (search: Record<string, unknown>): PipelineLogsSearch => {
    return {
      searchType: typeof search.searchType === 'string' ? search.searchType as PipelineSearchType : undefined,
      searchValue: typeof search.searchValue === 'string' ? search.searchValue : undefined,
      range: typeof search.range === 'string' ? search.range as QuickRangeLabel : undefined,
      logTypes: typeof search.logTypes === 'string' ? search.logTypes : undefined,
    };
  },
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: PipelineLogsComponent,
});

function getDateRangeFromQuickRange(rangeLabel: QuickRangeLabel | undefined): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const range = QUICK_RANGES.find(r => r.label === rangeLabel) || QUICK_RANGES[0];

  const start = range.hours <= 72
    ? subHours(now, range.hours)
    : subDays(now, range.hours / 24);

  return {
    dateFrom: start.toISOString(),
    dateTo: now.toISOString(),
  };
}

function PipelineLogsComponent() {
  const { getAccessTokenSilently, logout } = useAuth0();
  const navigate = useNavigate();
  const searchParams = useSearch({ from: '/pipeline-logs' });

  // Local form state
  const [searchType, setSearchType] = useState<PipelineSearchType>(searchParams.searchType || 'eventID');
  const [searchValue, setSearchValue] = useState(searchParams.searchValue || '');
  const [range, setRange] = useState<QuickRangeLabel>(searchParams.range || '24h');
  const [selectedLogTypes, setSelectedLogTypes] = useState<Set<PipelineLogType>>(() => {
    if (searchParams.logTypes) {
      const types = searchParams.logTypes.split(',').filter((t): t is PipelineLogType =>
        ['critical', 'error', 'warning', 'info', 'debug'].includes(t)
      );
      return new Set(types);
    }
    return new Set(); // empty = all types
  });

  // Expanded state for timeline entries
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Export all state
  const [exporting, setExporting] = useState(false);

  // Store state
  const logs = usePipelineLogsStore((s) => s.logs);
  const hasMore = usePipelineLogsStore((s) => s.hasMore);
  const meta = usePipelineLogsStore((s) => s.meta);
  const loading = usePipelineLogsStore((s) => s.loading);
  const error = usePipelineLogsStore((s) => s.error);
  const fetchLogs = usePipelineLogsStore((s) => s.fetchLogs);

  const handleSearch = useCallback(async () => {
    if (!searchValue.trim()) return;

    const logTypesParam = selectedLogTypes.size > 0
      ? Array.from(selectedLogTypes).join(',')
      : undefined;

    // Update URL with search params
    navigate({
      to: '/pipeline-logs',
      search: {
        searchType,
        searchValue: searchValue.trim(),
        range,
        logTypes: logTypesParam,
      },
    });

    try {
      const token = await getAccessTokenSilently();
      const { dateFrom, dateTo } = getDateRangeFromQuickRange(range);

      await fetchLogs(token, logout, {
        searchType,
        searchValue: searchValue.trim(),
        dateFrom,
        dateTo,
        logTypes: logTypesParam,
        limit: PIPELINE_CONFIG.DEFAULT_LIMIT,
      });
    } catch (err) {
      console.error('Error fetching pipeline logs:', err);
    }
  }, [searchType, searchValue, range, selectedLogTypes, getAccessTokenSilently, fetchLogs, logout, navigate]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleLoadMore = useCallback(async (newOffset: number) => {
    if (!searchValue.trim()) return;

    const logTypesParam = selectedLogTypes.size > 0
      ? Array.from(selectedLogTypes).join(',')
      : undefined;

    try {
      const token = await getAccessTokenSilently();
      const { dateFrom, dateTo } = getDateRangeFromQuickRange(range);

      await fetchLogs(token, logout, {
        searchType,
        searchValue: searchValue.trim(),
        dateFrom,
        dateTo,
        logTypes: logTypesParam,
        limit: PIPELINE_CONFIG.DEFAULT_LIMIT,
        offset: newOffset,
      });
    } catch (err) {
      console.error('Error fetching more pipeline logs:', err);
    }
  }, [searchType, searchValue, range, selectedLogTypes, getAccessTokenSilently, fetchLogs, logout]);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedIds(new Set(logs.map(log => log.id)));
  }, [logs]);

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  const toggleLogType = useCallback((type: PipelineLogType) => {
    setSelectedLogTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const applyPreset = useCallback((preset: QuickPreset) => {
    setSearchType(preset.searchType);
    setSearchValue(preset.searchValue);
    setRange(preset.range);
    setSelectedLogTypes(new Set(preset.logTypes));
  }, []);

  // Export to CSV (truncated message for readability)
  const exportToCSV = useCallback(() => {
    if (logs.length === 0) return;

    // Add metadata comment rows at top
    const metaRows = [
      `# Pipeline Logs Export - ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`,
      `# Search: ${searchType} = ${searchValue}`,
      meta ? `# Date Range: ${format(new Date(meta.dateFrom), 'MMM d yyyy HH:mm')} - ${format(new Date(meta.dateTo), 'MMM d yyyy HH:mm')}` : '',
      `# Showing ${logs.length} entries${hasMore ? ' (PARTIAL - more results available, use pagination to view all)' : ''}`,
      '',
    ].filter(Boolean);

    const headers = ['Timestamp', 'Severity', 'Category', 'Action', 'Event ID', 'Client ID', 'Encoder ID', 'Encoder Name', 'Asset ID', 'Error', 'Message Preview'];
    const rows = logs.map(log => {
      // Create a clean message preview - extract key info or truncate
      let messagePreview = log.extracted.errorMessage || '';
      if (!messagePreview) {
        // For non-JSON messages, use truncated raw text
        const cleaned = log.rawMessage.replace(/\n/g, ' ').replace(/"/g, "'");
        messagePreview = cleaned.length > 150 ? cleaned.slice(0, 150) + '...' : cleaned;
      }

      return [
        format(new Date(log.logDate), 'yyyy-MM-dd HH:mm:ss'),
        log.type,
        log.extracted.category || '',
        log.extracted.action || '',
        log.extracted.eventID || '',
        log.extracted.clientID || '',
        log.extracted.encoderID || '',
        log.extracted.encoderName || '',
        log.extracted.assetID || '',
        log.extracted.errorMessage || '',
        `"${messagePreview}"`,
      ];
    });

    const csvContent = [
      ...metaRows,
      headers.join(','),
      ...rows.map(row => row.join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const partialSuffix = hasMore ? '-partial' : '';
    link.download = `pipeline-logs-${searchType}-${searchValue}${partialSuffix}-${format(new Date(), "yyyyMMdd'T'HHmmss")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [logs, meta, hasMore, searchType, searchValue]);

  // Export to JSON (full data)
  const exportToJSON = useCallback(() => {
    if (logs.length === 0) return;

    const exportData = {
      exportDate: new Date().toISOString(),
      search: { type: searchType, value: searchValue },
      dateRange: meta ? { from: meta.dateFrom, to: meta.dateTo } : null,
      count: logs.length,
      hasMore,
      logs: logs.map(log => ({
        ...log,
        logDate: log.logDate,
      })),
    };

    const jsonContent = JSON.stringify(exportData, null, 2);

    const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pipeline-logs-${searchType}-${searchValue}-${format(new Date(), "yyyyMMdd'T'HHmmss")}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, [logs, meta, hasMore, searchType, searchValue]);

  // Export to Plain Text
  const exportToText = useCallback(() => {
    if (logs.length === 0) return;

    const header = [
      'Pipeline Logs Export',
      '═'.repeat(50),
      `Search: ${searchType} = ${searchValue}`,
      meta ? `Date Range: ${format(new Date(meta.dateFrom), 'MMM d, yyyy HH:mm')} - ${format(new Date(meta.dateTo), 'MMM d, yyyy HH:mm')}` : '',
      `Showing ${logs.length} entries${hasMore ? ' (more available)' : ''}`,
      '─'.repeat(50),
      '',
    ].join('\n');

    const entries = logs.map(log => {
      const lines = [
        `${format(new Date(log.logDate), 'HH:mm:ss')} [${log.type}] ${log.extracted.category || ''}${log.extracted.action ? '/' + log.extracted.action : ''}`,
      ];

      // Add extracted IDs
      const ids: string[] = [];
      if (log.extracted.eventID) ids.push(`Event: ${log.extracted.eventID}`);
      if (log.extracted.clientID) ids.push(`Client: ${log.extracted.clientID}`);
      if (log.extracted.encoderID) ids.push(`Encoder: ${log.extracted.encoderID}`);
      if (log.extracted.assetID) ids.push(`Asset: ${log.extracted.assetID.slice(0, 16)}...`);
      if (ids.length > 0) {
        lines.push(`  ${ids.join(' | ')}`);
      }

      // Add error message if present
      if (log.extracted.errorMessage) {
        lines.push(`  Error: ${log.extracted.errorMessage}`);
      }

      // Add message preview (truncated for readability)
      const msgPreview = log.rawMessage.slice(0, 200).replace(/\n/g, ' ');
      lines.push(`  ${msgPreview}${log.rawMessage.length > 200 ? '...' : ''}`);

      return lines.join('\n');
    }).join('\n\n');

    const textContent = header + entries;

    const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pipeline-logs-${searchType}-${searchValue}-${format(new Date(), "yyyyMMdd'T'HHmmss")}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  }, [logs, meta, hasMore, searchType, searchValue]);

  // Export ALL matching logs via server-side endpoint (bypasses pagination)
  const exportAll = useCallback(async () => {
    if (!meta || exporting) return;

    setExporting(true);
    try {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams();
      params.set('searchType', searchType);
      params.set('searchValue', searchValue);
      params.set('dateFrom', meta.dateFrom);
      params.set('dateTo', meta.dateTo);

      const logTypesParam = selectedLogTypes.size > 0
        ? Array.from(selectedLogTypes).join(',')
        : undefined;
      if (logTypesParam) params.set('logTypes', logTypesParam);

      const response = await fetch(`${API_BASE_URL}/api/pipeline/logs/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pipeline-logs-${searchType}-${searchValue}-all-${format(new Date(), "yyyyMMdd'T'HHmmss")}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export all failed:', err);
    } finally {
      setExporting(false);
    }
  }, [meta, exporting, searchType, searchValue, selectedLogTypes, getAccessTokenSilently]);

  // Pagination controls - reused at top and bottom of results
  const PaginationControls = ({ showLabel = false }: { showLabel?: boolean }) => {
    if (!meta || (meta.offset === 0 && !hasMore)) return null;
    const currentPage = Math.floor(meta.offset / PIPELINE_CONFIG.DEFAULT_LIMIT) + 1;
    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={loading || meta.offset === 0}
          onClick={() => handleLoadMore(Math.max(0, meta.offset - PIPELINE_CONFIG.DEFAULT_LIMIT))}
        >
          Previous
        </Button>
        <span className="text-sm text-muted-foreground min-w-[60px] text-center">
          Page {currentPage}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={loading || !hasMore}
          onClick={() => handleLoadMore(meta.offset + PIPELINE_CONFIG.DEFAULT_LIMIT)}
        >
          Next
        </Button>
        {showLabel && hasMore && (
          <span className="text-sm text-muted-foreground ml-2">· more available</span>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Pipeline Message Log</h1>
            <p className="text-muted-foreground">
              Search and debug pipeline events by entity ID
            </p>
          </div>

          {/* Quick Presets */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                Quick Searches
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-2" align="end">
              <div className="space-y-1">
                {QUICK_PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-muted"
                    onClick={() => applyPreset(preset)}
                  >
                    <div className="text-sm font-medium">{preset.label}</div>
                    <div className="text-xs text-muted-foreground">{preset.description}</div>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Search Form */}
        <div className="flex flex-wrap gap-3 items-end p-4 bg-muted/30 rounded-lg border">
          {/* Search Type */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Search By</label>
            <Select value={searchType} onValueChange={(v) => setSearchType(v as PipelineSearchType)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SEARCH_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Search Value */}
          <div className="space-y-1 flex-1 min-w-[200px]">
            <label className="text-xs text-muted-foreground">Value</label>
            <Input
              placeholder={`Enter ${searchType}...`}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full"
            />
          </div>

          {/* Date Range */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Time Range</label>
            <Select value={range} onValueChange={(v) => setRange(v as QuickRangeLabel)}>
              <SelectTrigger className="w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {QUICK_RANGES.map((r) => (
                  <SelectItem key={r.label} value={r.label}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Log Type Filter (Multi-select) */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Severity</label>
            <div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[140px] justify-between font-normal">
                  <span className="truncate">
                    {selectedLogTypes.size === 0
                      ? 'All Levels'
                      : selectedLogTypes.size === 1
                        ? LOG_TYPES.find(t => selectedLogTypes.has(t.value))?.label
                        : `${selectedLogTypes.size} selected`}
                  </span>
                  <ChevronDown className="h-4 w-4 opacity-50 ml-2 flex-shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[160px] p-2" align="start">
                <div className="space-y-1">
                  {LOG_TYPES.map((t) => (
                    <label
                      key={t.value}
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedLogTypes.has(t.value)}
                        onCheckedChange={() => toggleLogType(t.value)}
                      />
                      <span className={cn('h-2 w-2 rounded-full', t.color)} />
                      <span className="text-sm">{t.label}</span>
                    </label>
                  ))}
                  {selectedLogTypes.size > 0 && (
                    <>
                      <div className="border-t my-1" />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full h-7 text-xs"
                        onClick={() => setSelectedLogTypes(new Set())}
                      >
                        Clear All
                      </Button>
                    </>
                  )}
                </div>
              </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Search Button */}
          <Button onClick={handleSearch} disabled={loading || !searchValue.trim()}>
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Search
          </Button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <span className="text-destructive">{error}</span>
          </div>
        )}

        {/* Results */}
        {logs.length > 0 && (
          <div className="space-y-4">
            {/* Results Header */}
            <div className="flex justify-between items-center flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Showing {logs.length} results{hasMore && ' (more available)'}
                </span>
                {meta && (
                  <span className="text-xs text-muted-foreground">
                    ({format(new Date(meta.dateFrom), 'MMM d, HH:mm')} - {format(new Date(meta.dateTo), 'MMM d, HH:mm')})
                  </span>
                )}
                <span className="text-muted-foreground">·</span>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={expandAll}>
                  Expand All
                </Button>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={collapseAll}>
                  Collapse All
                </Button>
                <span className="text-muted-foreground">·</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
                      <Download className="h-3 w-3 mr-1" />
                      Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={exportToCSV}>
                      Export Page as CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToText}>
                      Export Page as Text
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToJSON}>
                      Export Page as JSON
                    </DropdownMenuItem>
                    {hasMore && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={exportAll} disabled={exporting}>
                          {exporting ? 'Exporting...' : 'Export All Matching (CSV)'}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <PaginationControls />
            </div>

            {/* Timeline */}
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-[19px] top-0 bottom-0 w-px bg-border" />

              {/* Log Entries */}
              <div className="space-y-1">
                {logs.map((log, index) => (
                  <TimelineEntry
                    key={log.id}
                    log={log}
                    isFirst={index === 0}
                    isLast={index === logs.length - 1}
                    isExpanded={expandedIds.has(log.id)}
                    onToggle={() => toggleExpanded(log.id)}
                  />
                ))}
              </div>
            </div>

            {/* Bottom Pagination */}
            <div className="flex justify-center pt-4">
              <PaginationControls showLabel />
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && logs.length === 0 && searchParams.searchValue && (
          <div className="text-center py-12 text-muted-foreground">
            No logs found for the specified search criteria.
          </div>
        )}

        {/* Initial State */}
        {!loading && logs.length === 0 && !searchParams.searchValue && (
          <div className="text-center py-12 text-muted-foreground">
            Enter a search value above to find pipeline logs.
          </div>
        )}
      </div>
    </Layout>
  );
}

// Timeline entry component
import type { PipelineLogEntry } from '@shared/types';
import { ChevronRight, ExternalLink } from 'lucide-react';
import { Link } from '@tanstack/react-router';

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-500 ring-red-500/20',
  error: 'bg-red-400 ring-red-400/20',
  warning: 'bg-amber-400 ring-amber-400/20',
  info: 'bg-blue-400 ring-blue-400/20',
  debug: 'bg-gray-400 ring-gray-400/20',
};

const ACTION_COLORS: Record<string, string> = {
  connect: 'text-green-600 dark:text-green-400',
  connected: 'text-green-600 dark:text-green-400',
  disconnected: 'text-orange-500 dark:text-orange-400',
  reconnected: 'text-green-600 dark:text-green-400',
  starting: 'text-blue-500 dark:text-blue-400',
  stopping: 'text-blue-500 dark:text-blue-400',
  completed: 'text-green-600 dark:text-green-400',
  processing: 'text-muted-foreground',
  scale: 'text-yellow-600 dark:text-yellow-400',
  error: 'text-red-500 dark:text-red-400',
};

const CATEGORY_COLORS: Record<string, string> = {
  encoder: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  transcode: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  events: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  egress: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  asset: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
  infrastructure: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  storage: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  email: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
  other: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

/**
 * Format raw message - pretty-print JSON if valid, otherwise return as-is
 */
function formatRawMessage(message: string): string {
  // Handle PLAYER: prefix
  const jsonPart = message.startsWith('PLAYER: ') ? message.slice(8) : message;

  // Try to parse and pretty-print JSON
  if (jsonPart.startsWith('{')) {
    try {
      const parsed = JSON.parse(jsonPart);
      const formatted = JSON.stringify(parsed, null, 2);
      return message.startsWith('PLAYER: ') ? `PLAYER: ${formatted}` : formatted;
    } catch {
      // Not valid JSON (truncated), return as-is
      return message;
    }
  }

  return message;
}

/**
 * Generate a preview string for the collapsed view
 */
function getMessagePreview(log: PipelineLogEntry): string | null {
  // If we have an extracted error message, show that
  if (log.extracted.errorMessage) {
    return log.extracted.errorMessage.slice(0, 60) + (log.extracted.errorMessage.length > 60 ? '...' : '');
  }

  // For plain text messages (not JSON), show a truncated preview
  const msg = log.rawMessage;
  if (!msg.startsWith('{') && !msg.startsWith('PLAYER: {')) {
    // Skip if message is very short or already covered by action/category
    if (msg.length < 20) return null;
    return msg.slice(0, 80) + (msg.length > 80 ? '...' : '');
  }

  // For JSON messages, try to extract a meaningful summary
  if (log.extracted.streamName) {
    return `stream: ${log.extracted.streamName}`;
  }
  if (log.extracted.assetID) {
    return `asset: ${log.extracted.assetID.slice(0, 12)}...`;
  }

  return null;
}

interface TimelineEntryProps {
  log: PipelineLogEntry;
  isFirst: boolean;
  isLast: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}

function TimelineEntry({ log, isExpanded, onToggle }: TimelineEntryProps) {
  const preview = getMessagePreview(log);

  return (
    <div className="relative pl-10">
      {/* Timeline dot */}
      <div
        className={cn(
          'absolute left-[15px] top-3 h-2.5 w-2.5 rounded-full ring-4 ring-background z-10',
          SEVERITY_COLORS[log.type] || 'bg-gray-400'
        )}
      />

      {/* Entry content */}
      <div
        className={cn(
          'p-3 rounded-lg border transition-all',
          isExpanded && 'bg-muted/30 border-foreground/10'
        )}
      >
        {/* Collapsed view - clickable header */}
        <div
          className="flex items-center gap-2 flex-wrap cursor-pointer hover:bg-muted/20 -m-1 p-1 rounded"
          onClick={onToggle}
        >
          {/* Expand indicator */}
          <ChevronRight
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform flex-shrink-0',
              isExpanded && 'rotate-90'
            )}
          />

          {/* Timestamp */}
          <span className="text-xs text-muted-foreground font-mono flex-shrink-0">
            {format(new Date(log.logDate), 'HH:mm:ss')}
          </span>

          {/* Category */}
          {log.extracted.category && (
            <span className={cn('text-xs px-1.5 py-0.5 rounded font-medium', CATEGORY_COLORS[log.extracted.category])}>
              {log.extracted.category}
            </span>
          )}

          {/* Action */}
          {log.extracted.action && (
            <span className={cn('text-xs font-medium', ACTION_COLORS[log.extracted.action])}>
              {log.extracted.action}
            </span>
          )}

          {/* Severity badge for non-info */}
          {log.type !== 'info' && (
            <Badge
              variant="outline"
              className={cn(
                'text-xs',
                log.type === 'critical' && 'border-red-500 text-red-500',
                log.type === 'error' && 'border-red-400 text-red-400',
                log.type === 'warning' && 'border-amber-500 text-amber-500',
                log.type === 'debug' && 'border-gray-400 text-gray-400'
              )}
            >
              {log.type}
            </Badge>
          )}

          {/* Truncated indicator */}
          {log.isTruncated && (
            <Badge variant="secondary" className="text-xs">
              truncated
            </Badge>
          )}

          {/* Message preview */}
          {preview && (
            <span className="text-xs text-muted-foreground truncate max-w-[300px] hidden sm:inline" title={preview}>
              {preview}
            </span>
          )}

          {/* Key IDs (right aligned) */}
          <div className="flex gap-2 ml-auto text-xs font-mono text-muted-foreground flex-shrink-0">
            {log.extracted.encoderName && (
              <span className="text-purple-600 dark:text-purple-400">{log.extracted.encoderName}</span>
            )}
            {log.extracted.eventID && <span>e:{log.extracted.eventID}</span>}
            {log.extracted.encoderID && !log.extracted.encoderName && <span>enc:{log.extracted.encoderID}</span>}
          </div>
        </div>

        {/* Expanded view */}
        {isExpanded && (
          <div className="mt-3 pt-3 border-t border-dashed space-y-3">
            {/* Extracted fields grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              {log.extracted.clientID && (
                <div>
                  <span className="text-muted-foreground">Client:</span>{' '}
                  <Link
                    to="/clients/$clientID"
                    params={{ clientID: log.extracted.clientID }}
                    className="font-mono text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {log.extracted.clientID}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </div>
              )}
              {log.extracted.eventID && (
                <div>
                  <span className="text-muted-foreground">Event:</span>{' '}
                  <span className="font-mono">{log.extracted.eventID}</span>
                </div>
              )}
              {log.extracted.encoderID && (
                <div>
                  <span className="text-muted-foreground">Encoder:</span>{' '}
                  <Link
                    to="/encoders/$encoderID"
                    params={{ encoderID: log.extracted.encoderID }}
                    className="font-mono text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {log.extracted.encoderID}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </div>
              )}
              {log.extracted.assetID && (
                <div>
                  <span className="text-muted-foreground">Asset:</span>{' '}
                  <span className="font-mono">{log.extracted.assetID.slice(0, 16)}...</span>
                </div>
              )}
              {log.extracted.instanceID && (
                <div>
                  <span className="text-muted-foreground">Instance:</span>{' '}
                  <span className="font-mono">{log.extracted.instanceID}</span>
                </div>
              )}
              {log.extracted.ipAddress && (
                <div>
                  <span className="text-muted-foreground">IP:</span>{' '}
                  <span className="font-mono">{log.extracted.ipAddress}</span>
                </div>
              )}
              {log.extracted.streamName && (
                <div>
                  <span className="text-muted-foreground">Stream:</span>{' '}
                  <span className="font-mono">{log.extracted.streamName}</span>
                </div>
              )}
              {log.extracted.transcodeID && (
                <div>
                  <span className="text-muted-foreground">Transcode:</span>{' '}
                  <span className="font-mono">{log.extracted.transcodeID.slice(0, 8)}...</span>
                </div>
              )}
              {log.extracted.errorFile && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">File:</span>{' '}
                  <span className="font-mono text-red-600 dark:text-red-400">
                    {log.extracted.errorFile}
                    {log.extracted.errorLine && `:${log.extracted.errorLine}`}
                  </span>
                </div>
              )}
              {log.extracted.errorClass && (
                <div>
                  <span className="text-muted-foreground">Class:</span>{' '}
                  <span className="font-mono text-red-600 dark:text-red-400">{log.extracted.errorClass}</span>
                </div>
              )}
              {log.extracted.errorMessage && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">Error:</span>{' '}
                  <span className="text-red-600 dark:text-red-400">{log.extracted.errorMessage}</span>
                </div>
              )}
            </div>

            {/* Raw message */}
            <div className="bg-muted/50 rounded p-2 overflow-x-auto">
              <pre className="text-xs font-mono whitespace-pre-wrap break-all leading-relaxed">
                {formatRawMessage(log.rawMessage)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
